import React from 'react';
import { Calendar, User, Clock, ArrowRight, Search, Tag, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';

const Blog = () => {
  const featuredPost = {
    id: 1,
    title: "The Future of Enterprise Software Development",
    excerpt: "Explore how AI, cloud-native architectures, and DevOps practices are reshaping enterprise software development in 2025 and beyond.",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1200&q=80",
    author: "Sarah Chen",
    date: "March 28, 2025",
    readTime: "8 min read",
    category: "Technology Trends"
  };

  const posts = [
    {
      id: 2,
      title: "Building Scalable Microservices Architecture",
      excerpt: "Learn the best practices for designing and implementing microservices that can handle enterprise-scale workloads.",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=800&q=80",
      author: "Michael Rodriguez",
      date: "March 25, 2025",
      readTime: "12 min read",
      category: "Architecture"
    },
    {
      id: 3,
      title: "Security Best Practices for Modern Web Applications",
      excerpt: "Essential security measures and implementation strategies to protect your web applications from emerging threats.",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=800&q=80",
      author: "Emily Thompson",
      date: "March 22, 2025",
      readTime: "10 min read",
      category: "Security"
    },
    {
      id: 4,
      title: "Optimizing Application Performance",
      excerpt: "Practical techniques and tools for improving the performance of your enterprise applications.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
      author: "David Chen",
      date: "March 20, 2025",
      readTime: "15 min read",
      category: "Performance"
    }
  ];

  const categories = [
    "All Posts",
    "Technology Trends",
    "Architecture",
    "Security",
    "Performance",
    "DevOps",
    "Cloud Computing"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
        </div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Insights & Updates</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Stay informed about the latest in enterprise software development,
              industry trends, and technical insights.
            </p>
            <div className="mt-8">
              <div className="relative max-w-xl mx-auto">
                <input
                  type="text"
                  placeholder="Search articles..."
                  className="w-full px-6 py-4 pl-12 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-slate-50 border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="flex items-center space-x-2 overflow-x-auto pb-4">
            <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
            {categories.map((category, index) => (
              <button
                key={index}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  index === 0
                    ? 'bg-yellow-400 text-slate-900'
                    : 'bg-white text-gray-600 hover:bg-yellow-400 hover:text-slate-900'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">Featured Article</h2>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[400px] rounded-xl overflow-hidden">
              <img
                src={featuredPost.image}
                alt={featuredPost.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div>
              <span className="inline-block px-4 py-2 bg-yellow-400 text-slate-900 rounded-full text-sm font-medium mb-4">
                {featuredPost.category}
              </span>
              <h3 className="text-3xl font-bold mb-4">{featuredPost.title}</h3>
              <p className="text-gray-600 mb-6">{featuredPost.excerpt}</p>
              <div className="flex items-center space-x-6 mb-8">
                <div className="flex items-center text-sm text-gray-500">
                  <User className="w-4 h-4 mr-2" />
                  {featuredPost.author}
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="w-4 h-4 mr-2" />
                  {featuredPost.date}
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="w-4 h-4 mr-2" />
                  {featuredPost.readTime}
                </div>
              </div>
              <Link
                to={`/blog/${featuredPost.id}`}
                className="inline-flex items-center px-6 py-3 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all group"
              >
                Read More
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Posts */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">Recent Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map(post => (
              <article
                key={post.id}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="relative h-48">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-yellow-400 text-slate-900 rounded-full text-sm font-medium mb-4">
                    {post.category}
                  </span>
                  <h3 className="text-xl font-bold mb-3">{post.title}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <User className="w-4 h-4 mr-2" />
                      {post.author}
                    </div>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-yellow-600 hover:text-yellow-700 font-medium"
                    >
                      Read More
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Stay Updated with Our Newsletter
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Get the latest insights and updates delivered directly to your inbox.
            </p>
            <form className="max-w-md mx-auto">
              <div className="flex gap-4">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-6 py-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all whitespace-nowrap"
                >
                  Subscribe
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;